import { MapEventType, Map as MaplibreMap } from "maplibre-gl";
import { MutableRefObject, useEffect } from "react";
import { MapRef } from "react-map-gl/maplibre";

import { useLatest } from "../";

import { isBrowser, noop } from "./helpers";

type MapListenerTargetValue = MapRef | MaplibreMap | undefined | null;
type MapListenerTarget =
  | (() => MapListenerTargetValue)
  | MapListenerTargetValue
  | MutableRefObject<MapListenerTargetValue>;

function resolveMapTarget(target?: MapListenerTarget): MapListenerTargetValue {
  if (!target) return undefined;

  if (typeof target === "function") {
    return target();
  }

  if (typeof target === "object" && "current" in target) {
    return target.current;
  }

  return target;
}

type MapEventName = keyof MapEventType;

function useMapListenerImpl<K extends MapEventName>(
  eventName: K,
  handler: (event: MapEventType[K]) => void,
  map: MapListenerTarget
) {
  const savedHandler = useLatest(handler);

  useEffect(() => {
    const target = resolveMapTarget(map);
    const mapInstance = target && "getMap" in target ? target.getMap() : target;

    if (!(mapInstance && "on" in mapInstance && "off" in mapInstance)) {
      return;
    }

    const eventListener = (event: MapEventType[K]) => savedHandler.current(event);

    mapInstance.on(eventName, eventListener);

    return () => {
      mapInstance.off(eventName, eventListener);
    };
  }, [savedHandler, eventName, map]);
}

export const useMapListener = isBrowser ? useMapListenerImpl : (noop as typeof useMapListenerImpl);
